import React, { useState, useEffect, useCallback } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import PropTypes from 'prop-types'

import updateRA from 'immutability-helper'
import { DecimalInput, NumberInput, SelectInput } from '../Inputs/Inputs'

import AppDispatcher from '../../../dispatchers/AppDispatcher'

import styles from '../../../../css/settings.module.css'

const DEFAULT_PHYSICAL = {
  fuelSensorType: '',
  fuelTankShape: 'Cylinder',
  fuelSensorRange: 2,
  fuelTankSize: 1,
  fuelLevelWarning: 40,
}

const FuelSensorControl = (props) => {
  const {
    device: {
      physical = DEFAULT_PHYSICAL,
    } = {},
    setValidity,
  } = props

  const [state, setState] = useState({
    fuelSensorType: physical.fuelSensorType,
    fuelTankShape: physical.fuelTankShape,
    fuelSensorRange: physical.fuelSensorRange,
    fuelTankSize: physical.fuelTankSize,
    fuelLevelWarning: physical.fuelLevelWarning,
    inputErrorFuelLevel: false,
    inputErrorTankSize: false,
    inputErrorSensorRange: false,
  })

  // Dispatch when relevant state values change
  useEffect(() => {
    const {
      fuelSensorType,
      fuelTankShape,
      fuelSensorRange,
      fuelTankSize,
      fuelLevelWarning,
      inputErrorFuelLevel,
      inputErrorTankSize,
      inputErrorSensorRange,
    } = state

    const physicals = {
      fuelTankShape,
      fuelSensorRange,
      fuelSensorType,
      fuelTankSize,
      fuelLevelWarning,
    }

    if (inputErrorFuelLevel || inputErrorTankSize || inputErrorSensorRange) {
      setValidity(false)
    } else {
      setValidity(true)
    }

    AppDispatcher.dispatch({
      action: 'change-physical',
      setting: physicals,
    })
  }, [
    state.fuelSensorType,
    state.fuelTankShape,
    state.fuelSensorRange,
    state.fuelTankSize,
    state.fuelLevelWarning,
    state.inputErrorFuelLevel,
    state.inputErrorTankSize,
    state.inputErrorSensorRange,
    setValidity,
  ])

  const childChange = useCallback((event) => {
    const { target: { name, value } } = event
    let newValue = value

    switch (name) {
      case 'fuelSensorType':
        newValue = value
        break

      case 'fuelTankShape':
        newValue = value
        break

      case 'fuelLevelWarning':
        newValue = Number(value)
        break

      case 'fuelTankSize':
        newValue = Number(value)
        break

      case 'fuelSensorRange':
        newValue = Number(value)
        break

      default:
        return
    }

    setState((prevState) => {
      let newState = updateRA(prevState, {
        [name]: { $set: newValue },
      })
      if (name === 'fuelSensorType') {
        if (value === '') {
          newState = updateRA(newState, {
            fuelTankShape: { $set: 'Cylinder' },
            fuelSensorRange: { $set: 5.557 },
            fuelTankSize: { $set: 5 },
            fuelLevelWarning: { $set: 0 },
          })
        } else if (value === 'Propane') {
          newState = updateRA(newState, {
            fuelTankShape: { $set: 'Cylinder' },
            fuelSensorRange: { $set: 5.557 },
            fuelTankSize: { $set: 5 },
            fuelLevelWarning: { $set: 40 },
          })
        } else if (value === 'Depth Sensor') {
          newState = updateRA(newState, {
            fuelTankShape: { $set: 'Cylinder' },
            fuelSensorRange: { $set: 2 },
            fuelTankSize: { $set: 1 },
            fuelLevelWarning: { $set: 40 },
          })
        }
      }

      if (name === 'fuelLevelWarning') {
        if (value < 0 || value > 100) {
          newState = updateRA(newState, {
            inputErrorFuelLevel: { $set: true },
          })
        } else {
          newState = updateRA(newState, {
            inputErrorFuelLevel: { $set: false },
          })
        }
      }

      if (name === 'fuelTankSize') {
        if (value <= 0 || value > 100) {
          newState = updateRA(newState, {
            inputErrorTankSize: { $set: true },
          })
        } else {
          newState = updateRA(newState, {
            inputErrorTankSize: { $set: false },
          })
        }
      }

      if (name === 'fuelSensorRange') {
        if (value <= 0 || value > 100) {
          newState = updateRA(newState, {
            inputErrorSensorRange: { $set: true },
          })
        } else {
          newState = updateRA(newState, {
            inputErrorSensorRange: { $set: false },
          })
        }
      }

      if (name === 'fuelTankShape') {
        if (value === 'Poly1CubicMeter') {
          newState = updateRA(newState, {
            fuelSensorRange: { $set: 2 },
            fuelTankSize: { $set: 0.86 },
            fuelLevelWarning: { $set: 40 },
          })
        } else {
          newState = updateRA(newState, {
            fuelSensorRange: { $set: 2 },
            fuelTankSize: { $set: 1 },
            fuelLevelWarning: { $set: 40 },
          })
        }
      }

      return newState
    })
  }, [])

  const {
    fuelLevelWarning,
    fuelSensorType,
    fuelTankShape,
    fuelTankSize,
    fuelSensorRange,
    inputErrorFuelLevel,
    inputErrorTankSize,
    inputErrorSensorRange,
  } = state

  const {
    intl: {
      formatMessage,
    },
  } = props
  const FUEL_SENSOR_TYPE = [
    {
      name: formatMessage({
        id: 'setting.fuelSensor.none.option',
        defaultMessage: 'None',
        description: 'None',
      }),
      value: '',
    },
    {
      name: formatMessage({
        id: 'setting.fuelSensor.diesel.option',
        defaultMessage: 'Diesel (SN111)',
        description: 'Depth Sensor',
      }),
      value: 'Depth Sensor',
    },
    {
      name: formatMessage({
        id: 'setting.fuelSensor.propane.option',
        defaultMessage: 'Liquid Propane Gas (SN110)',
        description: 'Propane',
      }),
      value: 'Propane',
    },
  ]

  const TANK_SHAPE = [
    {
      name: formatMessage({
        id: 'setting.fuelSensor.cube.option',
        defaultMessage: 'Cube',
        description: 'Cube',
      }),
      value: 'Cube',
    },
    {
      name: formatMessage({
        id: 'setting.fuelSensor.cylinder.option',
        defaultMessage: 'Cylinder',
        description: 'Cylinder',
      }),
      value: 'Cylinder',
    },
    {
      name: formatMessage({
        id: 'setting.fuelSensor.poly1CubicMeter.option',
        defaultMessage: 'Poly Tote 1m³',
        description: 'Poly Tote 1m³',
      }),
      value: 'Poly1CubicMeter',
    },
  ]

  return (
    <div>
      <div className={`${styles.inputArea} ${styles.paddedInputArea}`}>
        <section className={styles.heading}>
          <h3>
            <FormattedMessage
              id='setting.fuelSensor.label'
              defaultMessage='Fuel Sensor Calibration'
              description='Fuel Sensor Calibration label'
            />
          </h3>
        </section>
        <section>
          <h5>
            <FormattedMessage
              id='setting.fuelSensor.type.label'
              defaultMessage='Fuel Sensor Type'
              description='Fuel Sensor Type label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <SelectInput
              value={fuelSensorType}
              options={FUEL_SENSOR_TYPE}
              childChange={childChange}
              field='fuelSensorType'
              style={styles.controlSelectInput}
            />
          </div>
        </section>

        {fuelSensorType === 'Depth Sensor' && (
          <div>
            <br />
            <section>
              <h5>
                <FormattedMessage
                  id='setting.fuelSensor.tank.label'
                  defaultMessage='Tank Shape'
                  description='Tank Shape label'
                />
              </h5>
              <div className={styles.controlNumber}>
                <SelectInput
                  value={fuelTankShape}
                  options={TANK_SHAPE}
                  childChange={childChange}
                  field='fuelTankShape'
                  style={styles.controlSelectInput}
                />
              </div>
            </section>
            {fuelTankShape !== 'Poly1CubicMeter' && (
              <div>
                <br />
                <section>
                  <h5>
                    <FormattedMessage
                      id='setting.fuelSensor.tankSize'
                      defaultMessage='Fuel Tank Depth (m)'
                      description='Fuel Tank Depth label'
                    />
                  </h5>
                  <div className={`${styles.controlNumber} ${inputErrorTankSize && (styles.inputIssue)}`}>
                    <DecimalInput
                      value={fuelTankSize}
                      childChange={childChange}
                      field='fuelTankSize'
                      min={0}
                      style={styles.controlNumberInput}
                    />
                  </div>
                </section>
                <br />
                <section>
                  <h5>
                    <FormattedMessage
                      id='setting.fuelSensor.sensorRange'
                      defaultMessage='Fuel Sensor Range (m)'
                      description='Fuel Sensor Range label'
                    />
                  </h5>
                  <div className={`${styles.controlNumber} ${inputErrorSensorRange && (styles.inputIssue)}`}>
                    <DecimalInput
                      value={fuelSensorRange}
                      childChange={childChange}
                      field='fuelSensorRange'
                      min={0}
                      style={styles.controlNumberInput}
                    />
                  </div>
                </section>
              </div>
            )}
          </div>
        )}
        {(fuelSensorType === 'Propane' || fuelSensorType === 'Depth Sensor') && (
          <div>
            <br />
            <section>
              <h5>
                <FormattedMessage
                  id='setting.fuelSensor.warningLevel'
                  defaultMessage='Fuel Warning Level in App (%)'
                  description='Fuel Warning Level label'
                />
              </h5>
              <div className={`${styles.controlNumber} ${inputErrorFuelLevel && (styles.inputIssue)}`}>
                <NumberInput
                  value={fuelLevelWarning}
                  childChange={childChange}
                  field='fuelLevelWarning'
                  min={0}
                  max={100}
                  style={styles.controlNumberInput}
                />
              </div>
            </section>
          </div>
        )}
      </div>

    </div>

  )
}

FuelSensorControl.propTypes = {
  device: PropTypes.object.isRequired,
  setValidity: PropTypes.func.isRequired,
  intl: {
    formatMessage: PropTypes.func,
  },
}

export default injectIntl(FuelSensorControl)
